import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";

import { NoData } from "@/components/NoData/NoData";
import { isDefined } from "@/helpers/util";
import type { BarChartDataPoint } from "@/modules/analytics/components/BarChart";
import { BarChart } from "@/modules/analytics/components/BarChart";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { Trans, useTranslation } from "@/translations";

import { AnalyticsCard } from "../../../components/AnalyticsCard";

export function CommunicationsCard(): React.ReactNode {
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const { data } = useSuspenseQuery(analyticsQueries.weeklyMessages());
  const containerRef = useRef<HTMLDivElement>(null);

  const primaryData = useMemo(() => {
    if (!data) {
      return [];
    }

    const dataPoints: BarChartDataPoint[] = [];
    for (const point of data.weeklyCount) {
      const x = String(point.weekNumber);
      dataPoints.push({ x: x, y: isDefined(point.count) ? point.count : undefined });
    }

    return dataPoints;
  }, [data]);

  const noDataAvailable = !data || primaryData.every((x) => x.y === 0) || data.totalOverall === 0;

  return (
    <AnalyticsCard.Root ref={containerRef} variant="graph">
      <AnalyticsCard.Header
        title={t("page.analytics-overview.chart.communication.title")}
        tooltip={t("page.analytics-overview.chart.communication.title-tooltip")}
      />
      {noDataAvailable ? (
        <NoData />
      ) : (
        <figure className="flex flex-1 flex-col items-center">
          <p>
            <Trans
              i18nKey="page.analytics-overview.chart.communication.legend"
              values={{
                total: data.totalOverall,
              }}
              components={{
                bold: <strong className="inline text-body-emphasis" />,
              }}
            />
          </p>
          <BarChart
            dataPrimary={{
              data: primaryData,
            }}
            yTickMode="integer"
            formatXTick={(x) => `WK ${x}`}
            yLabel={t("page.analytics-overview.chart.communication.y-axis-label")}
            hoverLabel={(dataPoint) => (
              <span className="whitespace-pre-line">
                {t("page.analytics-overview.chart.communication.datapoint-label", {
                  amount: dataPoint.primary?.y || 0,
                  week: dataPoint.x,
                })}
              </span>
            )}
          />
        </figure>
      )}
    </AnalyticsCard.Root>
  );
}
